import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  DataTable,
  Field,
  FormRow,
  Input,
  RightDrawer,
} from '@projexn/ui';
import { api } from '../lib/api';

interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  unit: string;
  defaultUnitPriceCents: number;
  stockQty: number | null;
  notes: string | null;
}

export function InventoryPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const itemsQ = useQuery({
    queryKey: ['inventory'],
    queryFn: () => api.get<{ items: InventoryItem[] }>('/api/inventory'),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Inventory</h1>
          <p className="text-sm text-zinc-500">SKUs available for quoting and takeoffs.</p>
        </div>
        <Button onClick={() => setCreating(true)}>New SKU</Button>
      </header>

      <Card className="overflow-hidden">
        <DataTable
          rows={itemsQ.data?.items ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: 'sku', header: 'SKU', render: (r) => <code className="text-xs">{r.sku}</code> },
            { key: 'name', header: 'Name', render: (r) => r.name },
            { key: 'unit', header: 'Unit', render: (r) => r.unit },
            { key: 'cost', header: 'Unit cost', render: (r) => `$${(r.defaultUnitPriceCents / 100).toFixed(2)}` },
            { key: 'stock', header: 'Stock', render: (r) => r.stockQty?.toString() ?? '—' },
          ]}
          empty={<p className="px-6 py-12 text-center text-sm text-zinc-500">No SKUs yet.</p>}
        />
      </Card>

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="New SKU">
        <NewItemForm
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['inventory'] });
          }}
        />
      </RightDrawer>
    </div>
  );
}

function NewItemForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ sku: '', name: '', unit: 'ea', unitCostDollars: 0 });
  const mutate = useMutation({
    mutationFn: () =>
      api.post('/api/inventory', {
        sku: form.sku,
        name: form.name,
        unit: form.unit,
        defaultUnitPriceCents: Math.round(form.unitCostDollars * 100),
      }),
    onSuccess: onCreated,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutate.mutate();
      }}
      className="space-y-4"
    >
      <FormRow>
        <Field label="SKU" required>
          <Input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
        </Field>
        <Field label="Name" required>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Unit">
          <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
        </Field>
        <Field label="Unit cost ($)">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={form.unitCostDollars}
            onChange={(e) => setForm({ ...form, unitCostDollars: Number(e.target.value) })}
          />
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>Create</Button>
      </div>
    </form>
  );
}
